import type { Event } from '@sentry/browser';
import type { LogData, Tags, TagValue } from './types';

/** Set on errors by any Sentry client; host app's Sentry would skip errors we captured. */
const SENTRY_CAPTURED_MARKER = '__sentry_captured__';
/** Our own "already reported" flag; independent of Sentry's marker. */
const SCDK_CAPTURED_MARKER = '__scdk_captured__';

/**
 * Marks `error` with our non-enumerable `SCDK_CAPTURED_MARKER` flag.
 * Returns `true` when newly marked (first capture), `false` if already marked.
 * Primitives and non-extensible (frozen/sealed) objects always return `true`.
 */
export function markFirstCapture(error: unknown): boolean {
  if (error === null || typeof error !== 'object') return true;
  if (Object.prototype.hasOwnProperty.call(error, SCDK_CAPTURED_MARKER)) return false;
  try {
    Object.defineProperty(error, SCDK_CAPTURED_MARKER, {
      value: true,
      writable: true,
      configurable: true,
    });
  } catch (e) {
    // non-extensible: report anyway
  }
  return true;
}

/**
 * Runs `capture` with Sentry's shared "already captured" marker hidden, then restores it.
 * Sentry maintainers' recommended pattern for an independent client next to a host-page
 * Sentry (marker is writable/configurable so it can be reset before recapturing):
 * https://github.com/getsentry/sentry-javascript/issues/4525
 */
export function withHostMarkerHidden(error: unknown, capture: () => void) {
  if (error === null || typeof error !== 'object') {
    capture();
    return;
  }
  const hostMarker = Object.getOwnPropertyDescriptor(error, SENTRY_CAPTURED_MARKER);
  try {
    if (hostMarker) Reflect.deleteProperty(error, SENTRY_CAPTURED_MARKER);
    capture();
  } finally {
    // remove marker set by our sentry client, so the error can be recaptured by a host
    Reflect.deleteProperty(error, SENTRY_CAPTURED_MARKER);

    // restore host marker if it was present
    if (hostMarker) {
      Object.defineProperty(error, SENTRY_CAPTURED_MARKER, hostMarker);
    }
  }
}

/** Returns `tags` without its `undefined` entries. */
export function definedTags(tags: Record<string, TagValue>): Tags {
  const out: Tags = {};
  Object.keys(tags).forEach((key) => {
    const value = tags[key];
    if (value !== undefined) out[key] = value;
  });
  return out;
}

/** `values` keyed as `<prefix>.<key>`; nested objects flattened as `<prefix>.<key>.<sub>`. */
export function flattenKeys(
  prefix: string,
  values: LogData,
): Record<string, TagValue> {
  const out: Record<string, TagValue> = {};
  Object.keys(values).forEach((key) => {
    const value = values[key];
    if (typeof value === 'object') {
      Object.assign(out, flattenKeys(`${prefix}.${key}`, value));
    } else {
      out[`${prefix}.${key}`] = value as TagValue;
    }
  });
  return out;
}

/**
 * ` · ` built at runtime: the minifier would emit a literal `·`, the bundle's only non-ASCII
 * byte, which a page or CDN serving the script without a utf-8 charset decodes as `Â·`.
 */
const DETAIL_SEPARATOR = ` ${String.fromCharCode(0xb7)} `;

/**
 * Copy of `event` with `detail` appended to the value of its top-level exception (last in
 * `exception.values`), which Sentry shows as the Issue title. Copied, not mutated:
 * `dedupeIntegration` keeps the previous event by reference and compares exception values.
 */
export function appendToTopException(event: Event, detail: string): Event {
  const values = event.exception?.values;
  if (!values?.length) return event;
  const top = values[values.length - 1];
  const value = top.value ? `${top.value}${DETAIL_SEPARATOR}${detail}` : detail;
  return {
    ...event,
    exception: {
      ...event.exception,
      values: [...values.slice(0, -1), { ...top, value }],
    },
  };
}
